import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../lang";

const EASE = [0.16, 1, 0.3, 1];

export default function Founder() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section id="fondateur" ref={ref} className="border-t border-edge/60 px-6 py-24 md:px-10 md:py-40" data-testid="founder-section">
      <div className="grid grid-cols-1 gap-16 md:grid-cols-12">
        <div className="md:col-span-5">
          <div className="relative">
            <div className="absolute -left-3 -top-3 h-full w-full border border-signal/50" />
            <motion.div
              initial={{ clipPath: "inset(0 0 100% 0)" }}
              whileInView={{ clipPath: "inset(0 0 0% 0)" }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.1, ease: EASE }}
              className="relative aspect-[3/4] overflow-hidden border border-edge"
              data-testid="founder-image-frame"
            >
              <motion.img
                src="https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBleGVjdXRpdmUlMjBtb2Rlcm4lMjBwb3J0cmFpdCUyMGRhcmt8ZW58MHx8fHwxNzg5MzY4NTIyfDA&ixlib=rb-4.1.0&q=85"
                alt="Fondateur de TERRAIN — expert en stratégie de déploiement IA, forward deployment engineering et conformité AI Act"
                style={{ y, scale: 1.12 }}
                className="h-full w-full object-cover grayscale"
                data-testid="founder-image"
              />
              <div className="absolute inset-0 bg-void/25" />
            </motion.div>
          </div>
        </div>

        <div className="flex flex-col justify-center md:col-span-6 md:col-start-7">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-6 font-mono text-[11px] uppercase tracking-[0.25em] text-signal"
          >
            {t.founder.overline}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
            className="mb-8 font-display text-3xl font-semibold leading-tight tracking-tight md:text-5xl"
          >
            {t.founder.heading}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
            className="mb-10 max-w-lg text-base leading-relaxed text-muted-foreground"
          >
            {t.founder.body}
          </motion.p>
          <motion.blockquote
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
            className="mb-10 border-l-2 border-signal pl-6 font-display text-xl italic text-foreground md:text-2xl"
            data-testid="founder-quote"
          >
            {t.founder.quote}
          </motion.blockquote>
          <motion.ul
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.35 }}
            className="flex flex-wrap gap-3"
            data-testid="founder-chips"
          >
            {t.founder.chips.map((chip) => (
              <li
                key={chip}
                className="border border-edge px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-200 hover:border-signal hover:text-foreground"
              >
                {chip}
              </li>
            ))}
          </motion.ul>
        </div>
      </div>
    </section>
  );
}
